import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { calculateKwhWastePotentialBaselineForSelectedRange } from 'helpers/helpers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchHomePageInfo } from 'stores/sensor/homePageSlice';

const columns = ['kwh_5min', 'control'];

const CONTROL_LABELS = {
  0: 'DCV OFF',
  1: 'DCV ON - SOLAI',
  2: 'DCV ON - Manual',
  3: 'DCV ON - Schedule',
};

const formatFixedTrim = (value, digits) => {
  if (!Number.isFinite(value)) return '—';
  return value
    .toFixed(digits)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');
};

const roundToDigits = (value, digits = 1) => {
  if (!Number.isFinite(value)) return null;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const getCanonicalClusterValue = (values, digits = 1) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
  return roundToDigits(median, digits);
};

const SavingsDetailsModal = ({ buildingId, floorNumber, timeRange, totalCavHours, cavActiveHours = {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [expandedControllerIds, setExpandedControllerIds] = useState([]);

  const formatControlDistribution = control => {
    if (!control || typeof control !== 'object') {
      return '—';
    }

    const entries = Object.entries(control);
    if (entries.length === 0) {
      return '—';
    }

    return entries
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([key, value]) => {
        const label = CONTROL_LABELS[key] || key;
        const percentage = Number(value);
        const safePercentage = Number.isFinite(percentage) ? Math.round(percentage * 10) / 10 : 0;
        const formattedPercentage = formatFixedTrim(safePercentage, 1);
        return `${label}: ${formattedPercentage}%`;
      })
      .join(' | ');
  };

  const toggleExpanded = controllerId => {
    setExpandedControllerIds(prev =>
      prev.includes(controllerId) ? prev.filter(id => id !== controllerId) : [...prev, controllerId],
    );
  };

  const getControllerInfoValue = (controller, keys) => {
    for (const key of keys) {
      const value = controller?.[key] ?? controller?.info?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value);
      }
    }
    return '—';
  };

  const selectedCavActiveHours = useMemo(
    () => ({
      cav_start_time: cavActiveHours?.cav_start_time || {},
      cav_end_time: cavActiveHours?.cav_end_time || {},
    }),
    [cavActiveHours],
  );

  const calculateControllerBaseline = useCallback(
    controller => {
      const controllerParams = {
        area_m2: controller?.info?.area_m2 ?? controller?.area_m2,
        area_cfm: controller?.info?.area_cfm ?? controller?.area_cfm,
        unit_type: controller?.info?.unit_type ?? controller?.unit_type,
        area_fan_kw: controller?.info?.area_fan_kw ?? controller?.area_fan_kw,
        cooling_type: controller?.info?.cooling_type ?? controller?.cooling_type,
        heating_type: controller?.info?.heating_type ?? controller?.heating_type,
        controller_model: controller?.info?.controller_model ?? controller?.controller_model,
        mode: controller?.mode ?? controller?.info?.mode ?? 1,
      };

      const calculatedBaseline = calculateKwhWastePotentialBaselineForSelectedRange(
        selectedCavActiveHours,
        timeRange,
        controllerParams,
      );
      if (Number.isFinite(calculatedBaseline)) return calculatedBaseline;

      const backendBaseline = Number(controller?.kwh_waste_potential);
      return Number.isFinite(backendBaseline) ? backendBaseline : null;
    },
    [selectedCavActiveHours, timeRange],
  );

  useEffect(() => {
    if (!buildingId || buildingId === 'all') return;
    let isMounted = true;
    setLoading(true);
    setError(null);

    dispatch(
      fetchHomePageInfo({
        building_id: buildingId,
        floor_number: floorNumber,
        timeRange,
        columns,
      }),
    )
      .unwrap()
      .then(result => {
        if (!isMounted) return;
        setData(Array.isArray(result) ? result : []);
      })
      .catch(err => {
        if (!isMounted) return;
        setError(err?.message || 'Failed to load ventilation info');
        setData([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, buildingId, floorNumber, timeRange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rowsWithControl = (data || []).filter(item => {
      const control = item?.control;
      return control && typeof control === 'object' && Object.keys(control).length > 0;
    });

    const sorted = [...rowsWithControl].sort((a, b) => {
      const aFloor = Number(a.location?.floor_number);
      const bFloor = Number(b.location?.floor_number);
      const safeAFloor = Number.isFinite(aFloor) ? aFloor : Number.POSITIVE_INFINITY;
      const safeBFloor = Number.isFinite(bFloor) ? bFloor : Number.POSITIVE_INFINITY;
      if (safeAFloor !== safeBFloor) return safeAFloor - safeBFloor;

      const aName = (a.controller_name || '').toLowerCase();
      const bName = (b.controller_name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
    if (!q) return sorted;
    return sorted.filter(item => {
      const haystack = [
        item.controller_name,
        item.controller_mac,
        item.location?.building?.name,
        item.location?.area?.name,
        String(item.location?.floor_number ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, query]);
  const normalizedBaselineByControllerId = useMemo(() => {
    const baseRows = filtered
      .map(controller => {
        const controllerModel = getControllerInfoValue(controller, ['controller_model', 'model']);
        const displayedKwhBaseline = calculateControllerBaseline(controller);
        return {
          controllerId: controller?.controller_id,
          controllerModel,
          value: roundToDigits(displayedKwhBaseline, 1),
        };
      })
      .filter(row => row.controllerId && Number.isFinite(row.value));

    if (baseRows.length === 0) return {};

    const normalizedMap = {};
    const rowsByModel = baseRows.reduce((acc, row) => {
      const modelKey = row.controllerModel || '—';
      if (!acc[modelKey]) acc[modelKey] = [];
      acc[modelKey].push(row);
      return acc;
    }, {});

    for (const modelRows of Object.values(rowsByModel)) {
      const sorted = [...modelRows].sort((a, b) => a.value - b.value);
      const clusters = [];

      for (const row of sorted) {
        const lastCluster = clusters[clusters.length - 1];
        if (!lastCluster) {
          clusters.push([row]);
          continue;
        }

        const clusterAnchor = lastCluster[lastCluster.length - 1].value;
        const relativeTolerance = clusterAnchor * 0.03;
        const absoluteTolerance = 3;
        const tolerance = Math.max(absoluteTolerance, relativeTolerance);
        if (Math.abs(row.value - clusterAnchor) <= tolerance) {
          lastCluster.push(row);
        } else {
          clusters.push([row]);
        }
      }

      for (const cluster of clusters) {
        const canonical = getCanonicalClusterValue(
          cluster.map(item => item.value),
          1,
        );
        for (const item of cluster) {
          normalizedMap[item.controllerId] = canonical;
        }
      }
    }
    return normalizedMap;
  }, [filtered, calculateControllerBaseline]);

  if (loading) {
    return (
      <Box
        sx={{
          minWidth: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minWidth: 320, p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ color: 'text.secondary' }}>{filtered.length} controllers</Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search controller, area, floor, mac..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          sx={{ width: '320px' }}
        />
      </Stack>

      <TableContainer
        sx={{
          width: '100%',
          maxHeight: '60vh',
          overflowX: { xs: 'auto', lg: 'hidden' },
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          boxShadow: 'none',
        }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            width: '100%',
            minWidth: { xs: 1650, lg: 0 },
            '& thead th': {
              textAlign: 'center',
              height: 'auto',
              minHeight: 56,
              maxHeight: '2.4em',
              lineHeight: 1.2,
              verticalAlign: 'middle',
              writingMode: 'horizontal-tb',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>

          <TableHead
            sx={{
              backgroundColor: '#f9fafb',
              display: 'table-header-group',
              p: 0,
              '& th': {
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: '#f9fafb',
              },
            }}>
            <TableRow sx={{ minHeight: 56, '& > th': { py: 0.5 } }}>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Controller
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Location
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Control
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Occupied hours / CAV hours
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Total ON Time
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                kWh Use
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                kWh Baseline
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                kWh Saved
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                tCO2e Avoided
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Cost Saved
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Info
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map(controller => {
              const kwhUse = Number(controller?.kwh_5min);
              const occupiedRecords = Number(controller?.occupied_rocords);
              const occupiedHours = Number.isFinite(occupiedRecords) ? (occupiedRecords * 5) / 60 : null;
              const numberOfOnOffRecords = Number(controller?.number_of_on_off_records ?? controller?.on_off_records ?? 0);
              const totalOnHours = Number.isFinite(numberOfOnOffRecords) ? (numberOfOnOffRecords * 5) / 60 : null;
              const totalCavHoursValue = Number(totalCavHours);
              const occupiedHoursText =
                Number.isFinite(occupiedHours) && Number.isFinite(totalCavHoursValue)
                  ? `${formatFixedTrim(occupiedHours, 1)} h / ${formatFixedTrim(totalCavHoursValue, 1)} h`
                  : null;
              const totalOnTimeText = Number.isFinite(totalOnHours) ? `${formatFixedTrim(totalOnHours, 1)} h` : null;
              const locationText = `Floor ${controller.location?.floor_number ?? 'N/A'} • ${controller.location?.area?.name || 'Unassigned'}`;
              const controllerName = controller.controller_name;
              const rowKey = controller.controller_id;

              const controllerModel = getControllerInfoValue(controller, ['controller_model', 'model']);
              const unitType = getControllerInfoValue(controller, ['unit_type']);
              const cooling = getControllerInfoValue(controller, ['cooling', 'cooling_type']);
              const heating = getControllerInfoValue(controller, ['heating', 'heating_type']);
              const areaM2 = getControllerInfoValue(controller, ['area_m2']);
              const areaCfm = getControllerInfoValue(controller, ['area_cfm']);
              const areaFanKw = getControllerInfoValue(controller, ['area_fan_kw']);
              const areaM2Text = areaM2 === '—' ? '—' : `${areaM2} m²`;
              const areaCfmText = areaCfm === '—' ? '—' : `${areaCfm} CFM`;
              const areaFanKwText = areaFanKw === '—' ? '—' : `${areaFanKw} kW`;
              const normalizedUnitType = String(unitType || '')
                .trim()
                .toUpperCase();
              const showAreaCfm = normalizedUnitType !== 'AHU';
              const showAreaFanKw = normalizedUnitType !== 'FCU';
              const displayedKwhBaseline = calculateControllerBaseline(controller);
              const roundedKwhBaseline = roundToDigits(displayedKwhBaseline, 1);
              const normalizedKwhBaseline = Number.isFinite(normalizedBaselineByControllerId[rowKey])
                ? normalizedBaselineByControllerId[rowKey]
                : roundedKwhBaseline;
              const hasValidKwh = Number.isFinite(kwhUse) && Number.isFinite(normalizedKwhBaseline);
              const kwhSaved = hasValidKwh ? normalizedKwhBaseline - kwhUse : null;
              const tco2eAvoided = Number.isFinite(kwhSaved) ? kwhSaved * 0.459 : null;
              const costSaved = Number.isFinite(kwhSaved) ? 0.5 * kwhSaved : null;

              return (
                <React.Fragment key={rowKey}>
                  <TableRow hover sx={{ height: 48, '& > td': { py: 0 } }}>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'start' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        <Box
                          component="span"
                          onClick={() => navigate(`/controller/${rowKey}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 },
                          }}>
                          {controllerName}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: '1px solid #e0e0e0',
                        textAlign: 'start',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {locationText}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {formatControlDistribution(controller?.control)}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {occupiedHoursText || '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {totalOnTimeText || '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {Number.isFinite(kwhUse) ? `${formatFixedTrim(kwhUse, 1)} kWh` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {Number.isFinite(normalizedKwhBaseline) ? `${formatFixedTrim(normalizedKwhBaseline, 1)} kWh` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {Number.isFinite(kwhSaved) ? `${formatFixedTrim(kwhSaved, 1)} kWh` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {Number.isFinite(tco2eAvoided) ? `${formatFixedTrim(tco2eAvoided, 1)} KG` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      ₪{Number.isFinite(costSaved) ? `${Math.round(costSaved)}` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center' }}>
                      <IconButton size="small" onClick={() => toggleExpanded(rowKey)}>
                        {expandedControllerIds.includes(rowKey) ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon color="primary" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={11} sx={{ p: 0, borderBottom: 'none' }}>
                      <Collapse in={expandedControllerIds.includes(rowKey)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, backgroundColor: '#f7f8fb', borderTop: '1px solid #e0e0e0' }}>
                          <Table
                            stickyHeader
                            size="small"
                            sx={{
                              width: '100%',
                              tableLayout: 'fixed',
                              '& thead th': {
                                textAlign: 'center',
                                height: 'auto',
                                minHeight: 56,
                                maxHeight: '2.4em',
                                lineHeight: 1.2,
                                verticalAlign: 'middle',
                                writingMode: 'horizontal-tb',
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}>
                            <TableHead
                              sx={{
                                display: 'table-header-group',
                                p: 0,
                                '& th': {
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 3,
                                  backgroundColor: '#f7f8fb',
                                },
                              }}>
                              <TableRow sx={{ minHeight: 56, '& > th': { py: 0.5 } }}>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                  Controller Model
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                  Unit Type
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                  Cooling
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                  Heating
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                  Area m2
                                </TableCell>
                                {showAreaCfm ? (
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Area CFM
                                  </TableCell>
                                ) : null}
                                {showAreaFanKw ? (
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Area Fan kW
                                  </TableCell>
                                ) : null}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow sx={{ height: 44, '& > td': { py: 0 } }}>
                                <TableCell align="center">{controllerModel}</TableCell>
                                <TableCell align="center">{unitType}</TableCell>
                                <TableCell align="center">{cooling}</TableCell>
                                <TableCell align="center">{heating}</TableCell>
                                <TableCell align="center">{areaM2Text}</TableCell>
                                {showAreaCfm ? <TableCell align="center">{areaCfmText}</TableCell> : null}
                                {showAreaFanKw ? <TableCell align="center">{areaFanKwText}</TableCell> : null}
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SavingsDetailsModal;
